// DienstwunschModal — Modal zum Erstellen / Bearbeiten / Ansehen / Löschen eines Dienstwunsches.
//
// Verwendung:
//  - Mitarbeiter (eigene Zelle): Erstellen + Eigener Wunsch sichtbar bearbeiten/löschen
//  - Edit-User (fremder Wunsch): Read-Only mit Ablehnen + Notiz
import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Trash2, Save, Send, X as XIcon, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { dienstwunschApi } from '../../lib/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y.slice(-2)}`;
  } catch {
    return iso;
  }
};

export default function DienstwunschModal({
  open, onClose,
  mitarbeiter,        // { id, vorname, nachname }
  diensttag,          // { datum, dienst_art }
  abteilungId,
  fahrzeuge = [],
  existingWunsch,     // {id, fahrzeug_id, fahrzeug_name, notiz, status, einteiler_notiz, mitarbeiter_id, ...}
  canEditOthersWunsch, // boolean — User ist Einteiler und der Wunsch gehört einem anderen
  isOwner,             // boolean — der Modal-Inhalt gehört dem aktuell eingeloggten User
  canAdminDelete,      // boolean — User darf fremde Wünsche löschen (Admin/WaL)
  onSaved,             // optional callback nach Speichern/Löschen
}) {
  const [fahrzeugId, setFahrzeugId] = useState(existingWunsch?.fahrzeug_id || '');
  const [fahrzeugName, setFahrzeugName] = useState(existingWunsch?.fahrzeug_name || '');
  const [notiz, setNotiz] = useState(existingWunsch?.notiz || '');
  const [einteilerNotiz, setEinteilerNotiz] = useState(existingWunsch?.einteiler_notiz || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFahrzeugId(existingWunsch?.fahrzeug_id || '');
    setFahrzeugName(existingWunsch?.fahrzeug_name || '');
    setNotiz(existingWunsch?.notiz || '');
    setEinteilerNotiz(existingWunsch?.einteiler_notiz || '');
  }, [open, existingWunsch]);

  // Nur feste Fahrzeuge (keine Springer) zur Auswahl
  const auswahlFahrzeuge = useMemo(() => {
    return (fahrzeuge || []).filter(fz => !fz.ist_springer);
  }, [fahrzeuge]);

  const handleSelectFahrzeug = (fz) => {
    if (fahrzeugId === fz.id) {
      // Toggle off
      setFahrzeugId('');
      setFahrzeugName('');
    } else {
      setFahrzeugId(fz.id);
      setFahrzeugName(fz.funkrufname);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (existingWunsch && isOwner) {
        await dienstwunschApi.update(existingWunsch.id, {
          fahrzeug_id: fahrzeugId || null,
          fahrzeug_name: fahrzeugName || null,
          notiz,
        });
        toast.success('Wunsch aktualisiert');
      } else if (!existingWunsch) {
        await dienstwunschApi.create({
          datum: diensttag.datum,
          dienst_art: diensttag.dienst_art,
          fahrzeug_id: fahrzeugId || null,
          fahrzeug_name: fahrzeugName || null,
          notiz,
        }, abteilungId);
        toast.success('Wunsch gespeichert');
      }
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingWunsch) return;
    if (!window.confirm('Wunsch wirklich löschen?')) return;
    setIsSaving(true);
    try {
      await dienstwunschApi.remove(existingWunsch.id);
      toast.success('Wunsch gelöscht');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    if (!existingWunsch) return;
    setIsSaving(true);
    try {
      await dienstwunschApi.update(existingWunsch.id, {
        status: 'ABGELEHNT',
        einteiler_notiz: einteilerNotiz || null,
      });
      toast.success('Wunsch als abgelehnt markiert');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const title = existingWunsch
    ? (isOwner ? 'Meinen Dienstwunsch bearbeiten' : `Wunsch von ${existingWunsch.mitarbeiter_name || 'Mitarbeiter'}`)
    : 'Dienstwunsch eintragen';

  const istAbgelehnt = existingWunsch?.status === 'ABGELEHNT';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="dienstwunsch-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-orange-500" />
            {title}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium">{mitarbeiter?.vorname} {mitarbeiter?.nachname}</span>
            {' · '}
            {formatDate(diensttag?.datum)} ({diensttag?.dienst_art === 'N' ? 'Nacht' : 'Tag'})
            {istAbgelehnt && (
              <Badge variant="outline" className="ml-2 border-red-300 text-red-700 bg-red-50">Abgelehnt</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Fahrzeug-Auswahl */}
          <div>
            <p className="text-xs font-semibold mb-1.5 text-muted-foreground">Wunsch-Fahrzeug (optional)</p>
            <div className="grid grid-cols-3 gap-1.5">
              {auswahlFahrzeuge.map(fz => (
                <Button
                  key={fz.id}
                  type="button"
                  variant={fahrzeugId === fz.id ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'text-xs h-auto py-1.5 font-mono',
                    fahrzeugId === fz.id && 'ring-2 ring-orange-300',
                  )}
                  disabled={!isOwner && existingWunsch}
                  onClick={() => handleSelectFahrzeug(fz)}
                  data-testid={`wunsch-fz-${fz.id}`}
                >
                  {fz.funkrufname}
                </Button>
              ))}
            </div>
            {fahrzeugId && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Konkrete Funktion (StF/ATF/Ma/…) entscheidet der Einteiler.
              </p>
            )}
          </div>

          {/* Notiz */}
          <div>
            <p className="text-xs font-semibold mb-1.5 text-muted-foreground">Begründung / Notiz</p>
            <Textarea
              rows={3}
              placeholder="Optionale Begründung oder Hinweis"
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              disabled={!isOwner && !!existingWunsch}
              data-testid="wunsch-notiz"
              maxLength={500}
            />
            <p className="text-[10px] text-muted-foreground mt-1 text-right">{notiz.length}/500</p>
          </div>

          {/* Einteiler-Sicht: Notiz vom Einteiler */}
          {existingWunsch && (existingWunsch.einteiler_notiz || canEditOthersWunsch) && (
            <div className="rounded-md border bg-amber-50/40 p-2">
              <p className="text-xs font-semibold mb-1 text-amber-800">Einteiler-Notiz</p>
              {canEditOthersWunsch ? (
                <Textarea
                  rows={2}
                  placeholder="Optionale Notiz zur Ablehnung"
                  value={einteilerNotiz}
                  onChange={(e) => setEinteilerNotiz(e.target.value)}
                  data-testid="wunsch-einteiler-notiz"
                  maxLength={500}
                />
              ) : (
                <p className="text-xs whitespace-pre-wrap">{existingWunsch.einteiler_notiz}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          {/* Löschen-Button: Owner oder Admin+ */}
          {existingWunsch && (isOwner || canAdminDelete) && (
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={handleDelete}
              disabled={isSaving}
              data-testid="wunsch-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Löschen
            </Button>
          )}
          <div className="flex-1" />
          {/* Ablehnen — Einteiler */}
          {existingWunsch && canEditOthersWunsch && !istAbgelehnt && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleReject}
              disabled={isSaving}
              data-testid="wunsch-reject-btn"
              className="text-red-700 border-red-300"
            >
              <XIcon className="w-4 h-4 mr-1" /> Ablehnen
            </Button>
          )}
          {/* Speichern — Owner oder neu */}
          {(isOwner || !existingWunsch) && (
            <Button size="sm" onClick={handleSave} disabled={isSaving} data-testid="wunsch-save-btn">
              {existingWunsch ? <Save className="w-4 h-4 mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              {existingWunsch ? 'Speichern' : 'Wunsch absenden'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
